/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, IconButton, Container, Typography, Dialog, DialogTitle, DialogContent } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, useRef, } from "react";

import { useAppBarHeight,} from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import DatagridProductGroup from '../components/DatagridProductGroup';
import ProductGroupBusinessLogic from '../../../businessLogic/ProductGroupBusinessLogic';
import { IProductGroupGuiModel, IProductGroupPutGuiModel } from '../../../model/IProductGroupGuiModel';
import { NumericFormatWhole } from '../../../common/CustomFunctions';

const ProductGroup = (): JSX.Element => {

    const { organizationId } = useParams();

    const theme = useTheme();
    const [loader, setLoader] = useState(false);

    const [gridRows, setGridRows] = useState<IProductGroupGuiModel[]>([]);
    const [gridRowsErrors, setGridRowsErrors] = useState<string[]>([]);

    const dialogIdRef = useRef<number>(0);
    const [dialogName, setDialogName] = useState("");
    const [dialogVatRateId, setDialogVatRateId] = useState("");
    const [dialogTaxableSales, setDialogTaxableSales] = useState("");
    const [dialogTaxableDiscount, setDialogTaxableDiscounts] = useState("");
    const [dialogNonTaxableSales, setDialogNonTaxableSales] = useState("");
    const [dialogNonTaxableDiscount, setDialogNonTaxableDiscount] = useState("");

    const apiCallTypeRef = useRef<"put" | "post">("put");
    const [openDialog, setOpenDialog] = useState(false);
    const handleOpenDialog = (type: "put" | "post", id?: number, name?:string, vatRId?: string, tSales?: string, tDiscount?: string, nTSales?: string, ntDiscount?: string) => {
        apiCallTypeRef.current = type;
        dialogIdRef.current = id ? id : 0;
        setDialogName(name ? name : "")
        setDialogVatRateId(vatRId ? vatRId : "")
        setDialogTaxableSales(tSales ? tSales : "")
        setDialogTaxableDiscounts(tDiscount ? tDiscount : "")
        setDialogNonTaxableSales(nTSales ? nTSales : "")
        setDialogNonTaxableDiscount(ntDiscount ? ntDiscount : "")
        setOpenDialog(true);
    }
    const handleCloseDialog = () => {
        setOpenDialog(false);
    }
    const { t, i18n } = useTranslation();

    //########################################################################################
    const appBarHeight = useAppBarHeight();
    //########################################################################################
    //########################################################################################
    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        getProductGroups(isMounted);

        return () => { isMounted = false };

    }, [organizationId]);
    const getProductGroups = async (isMounted: boolean) => {
        const logic = new ProductGroupBusinessLogic();
        setLoader(true);
        const result = await logic.getProductGroups(organizationId ? organizationId : "");
        setLoader(false);
        if (result.hasErrors) {
            if (isMounted) setGridRowsErrors(result.errors);
        } else {
            if (isMounted) setGridRowsErrors([]);
            if (isMounted) setGridRows(result.value);
        }
    }

    const validate = (): boolean => {
        if (dialogName !== "" &&
            dialogVatRateId !== "" &&
            dialogTaxableSales !== "" &&
            dialogTaxableDiscount !== "" &&
            dialogNonTaxableSales !== "" &&
            dialogNonTaxableDiscount !== "") {
            return true;
        } else {
            return false;
        }
    }

    const postProductGroup = async () => {

        if (validate()) {
            const logic = new ProductGroupBusinessLogic();
            setLoader(true);
            const result = await logic.postProductGroup(
                organizationId ? organizationId : "",
                dialogName,
                dialogVatRateId,
                dialogTaxableSales,
                dialogTaxableDiscount,
                dialogNonTaxableSales,
                dialogNonTaxableDiscount);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("basic.errorDataSave") + result.errors.join(", "));
            } else {
                handleCloseDialog();
                getProductGroups(true);
            }
        } else {
            toast(t("basic.inputErrors"));
        }
    }
    const putProductGroup = async () => {

        if (validate()) {
            const logic = new ProductGroupBusinessLogic();

            const data: IProductGroupPutGuiModel = {

                name: { value: dialogName },
                vatId: { value: dialogVatRateId },
                taxableSales: { value: dialogTaxableSales },
                taxableDiscount: { value: dialogTaxableDiscount },
                nonTaxableSales: { value: dialogNonTaxableSales },
                nonTaxableDiscount: { value: dialogNonTaxableDiscount }
            }

            setLoader(true)
            const result = await logic.putProductGroup(organizationId ? organizationId : "", dialogIdRef.current, data);
            setLoader(false);
            if (result.hasErrors) {
                toast(t("basic.errorDataSave") + result.errors.join(", "));
            } else {
                handleCloseDialog();
                getProductGroups(true);
            }
        } else {
            toast(t("basic.inputErrors"));
        }
    }

    return <main>
        <Grid container direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                width: "100%",
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            <Container component={Paper} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Grid container padding={theme.spacing(4)} justifyContent="space-between">
                    <Grid>
                        <Typography variant="h6">{t("sidemenu.productGroups")}</Typography>
                    </Grid>
                    <Grid>
                        <Button onClick={() => handleOpenDialog("post", undefined)} variant="contained">{t("basic.add")}</Button>
                    </Grid>
                </Grid>
                <Grid container paddingBottom={theme.spacing(2.5)} flex="1 1 auto" minHeight="40rem">
                    <DatagridProductGroup
                        gridRows={gridRows}
                        gridRowsErrors={gridRowsErrors}
                        loader={loader}
                        handleOpenDialog={handleOpenDialog} />
                </Grid>
            </Container>
        </Grid>
        <Dialog open={openDialog} onClose={handleCloseDialog}>
            <DialogTitle>
                <Stack direction="row" spacing={6} alignItems="center">
                    <Typography variant="h6">
                        {apiCallTypeRef.current === "post" ? t("productGroup.createProductGroup") : t("productGroup.editProductGroup")}
                    </Typography>
                    <IconButton onClick={handleCloseDialog}><Close/></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={3}>
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogName}
                        error={dialogName === ""}
                        onChange={e => setDialogName(e.target.value)}
                        sx={{
                            minWidth: "6.5rem", marginTop: theme.spacing(2),
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        id="name" label={t("basic.name")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogVatRateId}
                        error={dialogVatRateId === ""}
                        onChange={e => setDialogVatRateId(e.target.value)}
                        sx={{
                            minWidth: "6.5rem",
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        id="vatId" label={t("productGroup.vatRateId")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogTaxableSales}
                        error={dialogTaxableSales === ""}
                        onChange={e => setDialogTaxableSales(e.target.value)}
                        sx={{
                            minWidth: "6.5rem",
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        id="taxableSales" label={t("productGroup.taxableSales")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogTaxableDiscount}
                        error={dialogTaxableDiscount === ""}
                        onChange={e => setDialogTaxableDiscounts(e.target.value)}
                        sx={{
                            minWidth: "6.5rem",
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        id="taxableDiscount" label={t("productGroup.taxableDiscount")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogNonTaxableSales}
                        error={dialogNonTaxableSales === ""}
                        onChange={e => setDialogNonTaxableSales(e.target.value)}
                        sx={{
                            minWidth: "6.5rem",
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        id="nonTaxableSales" label={t("productGroup.nonTaxableSales")} />
                    <TextField
                        size="small"
                        variant="outlined"
                        value={dialogNonTaxableDiscount}
                        error={dialogNonTaxableDiscount === ""}
                        onChange={e => setDialogNonTaxableDiscount(e.target.value)}
                        sx={{
                            minWidth: "6.5rem",
                            backgroundColor: theme.palette.background.paper,
                        }}
                        InputLabelProps={{ shrink: true, }}
                        InputProps={{ inputComponent: NumericFormatWhole as any }}
                        id="nonTaxableDiscount" label={t("productGroup.nonTaxableDiscount")} />
                    <Button fullWidth variant="contained"
                        onClick={apiCallTypeRef.current === "put" ? putProductGroup : postProductGroup}>
                        {t("basic.save")}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    </main>

}
export default ProductGroup;